// client/src/coms/voip/voiceclient_parts/voiceReliability.js
// Pure decision helpers for voice join / disconnect / rejoin behaviour (unit tested).
// Numeric reasons follow livekit-client's DisconnectReason enum.

export const DISCONNECT = {
  UNKNOWN_REASON: 0,
  CLIENT_INITIATED: 1,
  DUPLICATE_IDENTITY: 2,
  SERVER_SHUTDOWN: 3,
  PARTICIPANT_REMOVED: 4,
  ROOM_DELETED: 5,
  STATE_MISMATCH: 6,
  JOIN_FAILURE: 7,
  MIGRATION: 8,
  SIGNAL_CLOSE: 9,
  ROOM_CLOSED: 10,
  CONNECTION_TIMEOUT: 14,
  MEDIA_FAILURE: 15,
};

export const MAX_VOICE_REJOIN_ATTEMPTS = 5;

// -> { rejoin: boolean, message: string }
export function classifyVoiceDisconnect(reason) {
  const r = Number(reason);
  switch (r) {
    case DISCONNECT.CLIENT_INITIATED:
      return { rejoin: false, message: '' };
    case DISCONNECT.DUPLICATE_IDENTITY:
      return { rejoin: false, message: 'You joined voice from another tab or device, so this one was disconnected.' };
    case DISCONNECT.PARTICIPANT_REMOVED:
      return { rejoin: false, message: 'You were removed from the voice channel.' };
    case DISCONNECT.ROOM_DELETED:
    case DISCONNECT.ROOM_CLOSED:
      return { rejoin: false, message: 'The voice channel was closed.' };
    case DISCONNECT.JOIN_FAILURE:
      return { rejoin: false, message: 'Could not join the voice channel.' };
    case DISCONNECT.SERVER_SHUTDOWN:
    case DISCONNECT.MIGRATION:
    case DISCONNECT.SIGNAL_CLOSE:
    case DISCONNECT.STATE_MISMATCH:
    case DISCONNECT.CONNECTION_TIMEOUT:
    case DISCONNECT.MEDIA_FAILURE:
    case DISCONNECT.UNKNOWN_REASON:
    default:
      // Undefined reason = the SDK gave up reconnecting after a network loss.
      return { rejoin: true, message: 'Voice connection lost. Reconnecting…' };
  }
}

export function voiceRejoinDelayMs(attempt, random = Math.random) {
  const base = Math.min(15000, 1000 * Math.pow(2, Math.max(0, attempt)));
  return Math.round(base * (0.8 + random() * 0.4));
}

// Microphone problems never block joining: the user is connected listen-only with an explanation.
export function describeMicFailure(err) {
  const name = String((err && (err.name || err.code)) || '');
  const msg = String((err && err.message) || '');
  if (name === 'NotAllowedError' || name === 'SecurityError' || /permission|denied/i.test(msg)) {
    return 'Microphone access is blocked, so you joined listen-only. Allow microphone access in your browser to talk.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || /not ?found|no device/i.test(msg)) {
    return 'No microphone was found, so you joined listen-only.';
  }
  if (name === 'NotReadableError' || /in use|could not start/i.test(msg)) {
    return 'Your microphone is in use by another app, so you joined listen-only.';
  }
  return 'Your microphone could not be started, so you joined listen-only.';
}

export const NO_SPEAK_PERMISSION_MESSAGE = 'You do not have permission to speak in this channel, so you joined listen-only.';

export function describeVoiceJoinError(err) {
  const status = Number(err && err.status);
  const code = String((err && err.data && err.data.code) || '');
  if (code === 'voice_not_configured' || status === 503) return 'Voice is not available on this server right now.';
  if (status === 404) return 'That voice channel no longer exists or you cannot see it.';
  if (status === 403) return String((err && err.data && err.data.error) || 'You do not have permission to join this voice channel.');
  if (status === 401) return 'Please sign in again to join voice.';
  const msg = String((err && err.message) || '');
  if (/timeout|timed out/i.test(msg)) return 'Connecting to voice timed out. Check your connection and try again.';
  if (/could not establish|pc connection|ice|websocket/i.test(msg)) {
    return 'Could not connect to the voice server. A firewall or VPN may be blocking it.';
  }
  return msg || 'Could not join voice.';
}
